import React, { useState } from "react";
import {
  IonModal,
  IonButton,
  IonIcon,
  IonText,
} from "@ionic/react";
import {
  peopleOutline,
  chatbubblesOutline,
  swapHorizontalOutline,
  rocketOutline,
} from "ionicons/icons";

interface WerkerOnboardingProps {
  isOpen: boolean;
  onFinish: () => void;
  onStartTrial: () => void;
}

const slides = [
  {
    icon: peopleOutline,
    title: "Find Your Associates",
    body: "Head to the Associates tab and search for people you work with. Send them an associate request to connect.",
  },
  {
    icon: chatbubblesOutline,
    title: "Stay Connected",
    body: "Once you have associates, you can direct message them and receive job offers and announcements from schedulers.",
  },
  {
    icon: swapHorizontalOutline,
    title: "Switch to Scheduler Mode",
    body: "Use the toggle in the top-left corner to switch between Werker and Scheduler mode. The header color changes so you always know which mode you're in.",
  },
  {
    icon: rocketOutline,
    title: "Try Scheduler Pro",
    body: "Need to create jobs, build crews, and send announcements? Upgrade to Scheduler Pro and get a 30-day free trial. You can always keep using Werker for free.",
    isUpsell: true,
  },
];

const WerkerOnboarding: React.FC<WerkerOnboardingProps> = ({ isOpen, onFinish, onStartTrial }) => {
  const [step, setStep] = useState(0);

  const handleNext = () => {
    if (step < slides.length - 1) {
      setStep(step + 1);
    } else {
      setStep(0);
      onFinish();
    }
  };

  const handleSkip = () => {
    setStep(0);
    onFinish();
  };

  const current = slides[step];
  const isUpsell = (current as any).isUpsell === true;

  return (
    <IonModal isOpen={isOpen} backdropDismiss={false}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          padding: "40px 28px",
          textAlign: "center",
        }}
      >
        <IonIcon
          icon={current.icon}
          style={{
            fontSize: "72px",
            color: isUpsell ? "var(--ion-color-secondwarning)" : "var(--ion-color-warning)",
            marginBottom: 24,
          }}
        />
        <IonText>
          <h2 style={{ fontWeight: 700, fontSize: "22px", margin: "0 0 12px" }}>
            {current.title}
          </h2>
        </IonText>
        <IonText color="medium">
          <p style={{ fontSize: "16px", lineHeight: 1.5, margin: "0 0 32px" }}>
            {current.body}
          </p>
        </IonText>

        {/* Dots */}
        <div style={{ display: "flex", gap: 8, marginBottom: 32 }}>
          {slides.map((_, i) => (
            <div
              key={i}
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background:
                  i === step
                    ? "var(--ion-color-warning)"
                    : "var(--ion-color-medium)",
                opacity: i === step ? 1 : 0.3,
              }}
            />
          ))}
        </div>

        {isUpsell ? (
          <>
            <IonButton
              expand="block"
              color="secondwarning"
              onClick={() => {
                setStep(0);
                onStartTrial();
              }}
              style={{ width: "100%", maxWidth: 300 }}
            >
              Start Free Trial
            </IonButton>
            <IonButton
              fill="clear"
              color="medium"
              onClick={() => {
                setStep(0);
                onFinish();
              }}
              style={{ marginTop: 8 }}
            >
              Continue as Werker — Free
            </IonButton>
          </>
        ) : (
          <>
            <IonButton expand="block" color="warning" onClick={handleNext} style={{ width: "100%", maxWidth: 300 }}>
              {step < slides.length - 2 ? "Next" : "Next"}
            </IonButton>

            {step < slides.length - 1 && (
              <IonButton fill="clear" color="medium" onClick={handleSkip} style={{ marginTop: 8 }}>
                Skip
              </IonButton>
            )}
          </>
        )}
      </div>
    </IonModal>
  );
};

export default WerkerOnboarding;
